// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { ArticleConstants } from '@article/constants/article-constants';
import { LabelEditorMode } from '../../label/label-editor/label-editor.component';
import { ArrayUtils } from '@shared/util/array-utils';
import { ProjectModel } from '../../../model/project.model';
import { ProjectHelper } from '../../../helper/project.helper';
import { LabelEditorStateManager } from '../../label/label-editor/service/label-editor-state.manager';
import { AccessoryEditorStateManager } from '../accessory/service/accessory-editor-state.manger';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { InsertArticleHelperFactory } from '@article/helper/insert-article-helper.factory';
import { ConnectionAreaUsageVO, ConnectionAreaVO, MetaElementUsageIdentityVO, SelectionMetaElementUsageIdentity, TextureAreaVO } from '@shared/vo/meta-element.vo';
import { SelectionArticleGroupIdentity } from '../model/vo/selection-article-group-identity';
import { SelectionData, SelectionModel } from '../model/selection.model';
import { Connection } from '@3d-scene/model3d/connection';
import { MetaElementUtils } from '@article/components/connections/connection-creation/meta-element.utils';
import { ArticleUsageVO } from '@article/model/vo/article.vo';
import { PropertyVO } from '../model/vo/property.vo';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { InsertArticleHelper } from '@article/helper/insert-article.helper';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { PropertyUtils } from '@article/util/property-utils';
import { ArticleConnectionPropertyDef } from '@article/constants/article-connection-property-def';
import { AccessoryEditorState } from '../accessory/accessory.component';
import { CollisionHelper } from '@3d-scene/helper/collision.helper';
import { Material } from 'three';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { ArticleConnectionService } from '@3d-scene/model3d/article-connection.service';
import { ArticleContainerService } from '@3d-scene/service/article-container.service';
import { MetaElementUsageUtils } from '../../../util/meta-element-usage-utils';
import { ConnectionInfoService } from '@3d-scene/model3d/service/connection-info.service';
import { CompositeArticleInfoUtils } from '@article/util/composite-article-info-utils';
import { ArticleTypeIds } from '@article/constants/article-type-ids';

export type SelectionId = MetaElementUsageIdentityVO | SelectionMetaElementUsageIdentity | SelectionArticleGroupIdentity | SelectionId[];

@Injectable({ providedIn: 'root' })
export class SelectionModelService {
  private _selectionEnabledControlHandlers: { (): boolean; } [] = [];

  public addSelectionEnabledControlHandler(handler:{ (): boolean; }): void {
    this._selectionEnabledControlHandlers.push(handler);
  }

  public removeSelectionEnabledControlHandler(handler:{ (): boolean; }): void {
    ArrayUtils.removeItem(handler, this._selectionEnabledControlHandlers);
  }

  public isSelectionEnabled(): boolean {
    if (this.labelEditorStateManager.labelEditorMode === LabelEditorMode.TEXT_CHECK_FAILED) {
      return false;
    }
    for (const selectionEnabledControlHandler of this._selectionEnabledControlHandlers) {
      const selectionEnabled: boolean = selectionEnabledControlHandler();
      if (!selectionEnabled) {
        return false;
      }
    }
    return true;
  }

  constructor(
    private selectionModel: SelectionModel,
    private projectModel: ProjectModel,
    private projectHelper: ProjectHelper,
    private labelEditorStateManager: LabelEditorStateManager,
    private accessoryEditorStateManager: AccessoryEditorStateManager,
    private containerService: ContainerService,
    private compositeArticleInfoUtils: CompositeArticleInfoUtils,
    private articleContainerUtils: ArticleContainerUtils,
    private insertArticleHelperFactory: InsertArticleHelperFactory,
    private articleConnectionService: ArticleConnectionService,
    private articleContainerService: ArticleContainerService,
    private connectionInfoService: ConnectionInfoService
  ) {}

  public selectObject(selectionId: SelectionId, ctrlClamped: boolean = false, shiftClamped: boolean = false, forceTextureSelection: boolean = false): void {
    if (!this.isSelectionEnabled()) {
      return;
    }

    let newSelectionId: SelectionId | undefined = selectionId;

    if (selectionId instanceof MetaElementUsageIdentityVO) {
      if (!this.isMetaElementSelectionAvailable(selectionId, ctrlClamped, forceTextureSelection)) {
        // if selection of connection area is not available - then select article that contains this area
        newSelectionId = this.createArticleSelectionIdByMetaElementUsageIdentity(selectionId);
        // if that article is already selected: deselect it instead (to keep behaviour consistent with directly clicking an article)
        if (newSelectionId instanceof SelectionArticleGroupIdentity) {
          const selectionArticleGroupId: SelectionArticleGroupIdentity = newSelectionId;
          const objectToDeselect = this.selectionModel.selectedObjects
            .find((selectedObject) => selectedObject instanceof SelectionArticleGroupIdentity && selectedObject.equals(selectionArticleGroupId));
          if (objectToDeselect) {
            this.unSelectObject(newSelectionId);
          }
        }
      } else if (shiftClamped) {
        newSelectionId = this.createSelectionIdForShiftMultiSelectionForConnections(selectionId);
      }
    }
    if (shiftClamped) {
      newSelectionId = this.createSelectionIdForShiftMultiSelectionForArticles(newSelectionId);
    }

    if (!newSelectionId) {
      return;
    }

    if (!this.projectModel.isAdminView) {
      newSelectionId = this.expandSelectionBySelectionSet(newSelectionId);
    }
    this.selectObjectById(newSelectionId, ctrlClamped);
  }

  private expandSelectionBySelectionSet(selectionId: SelectionId): SelectionId {
    if (selectionId instanceof SelectionArticleGroupIdentity) {
      return selectionId;
    }
    const expandedSelectionIds: SelectionId[] = [];
    if (selectionId instanceof MetaElementUsageIdentityVO) {
      this.addSelectionIdsBySet(selectionId, expandedSelectionIds);
    } else if (selectionId instanceof Array) {
      for (const item of selectionId) {
        if (item instanceof MetaElementUsageIdentityVO) {
          this.addSelectionIdsBySet(item, expandedSelectionIds);
        } else {
          expandedSelectionIds.push(item);
        }
      }
    } else {
      expandedSelectionIds.push(selectionId);
    }
    return expandedSelectionIds;
  }

  private selectObjectById(selectionId: SelectionId, appendMode: boolean): void {
    const oldSelectedObjects: SelectionId[] = appendMode ? [] : this.selectionModel.selectedObjects.concat();
    const objectsToDeselectMask: boolean[] = !oldSelectedObjects ? [] : new Array<boolean>(oldSelectedObjects.length);
    if (objectsToDeselectMask) {
      ArrayUtils.fillArray(objectsToDeselectMask, 0, objectsToDeselectMask.length, true);
    }
    if (!appendMode) {
      this.selectionModel.selectedObjects = [];
    }
    const objectsToDispatchSelectMessage: SelectionId[] = [];
    this.internalSelectObject(selectionId, appendMode, oldSelectedObjects, objectsToDeselectMask, objectsToDispatchSelectMessage);

    const objectsToDispatchDeselectMessage: SelectionId = [];
    if (objectsToDeselectMask) {
      for (let i: number = 0; i < objectsToDeselectMask.length; i++) {
        if (objectsToDeselectMask[i]) {
          objectsToDispatchDeselectMessage.push(oldSelectedObjects[i]);
        }
      }
    }

    this.dispatchObjectMessages(objectsToDispatchSelectMessage, objectsToDispatchDeselectMessage);
  }

  private internalSelectObject(
    selectionId: SelectionId,
    appendMode: boolean,
    oldSelectedObjects: SelectionId[],
    objectsToDeselectMask: boolean[],
    objectsToDispatchSelectMessage: SelectionId[]
  ): void {
    if (selectionId == null) {
      return;
    }
    if (selectionId instanceof Array) {
      for (const item of selectionId) {
        this.internalSelectObject(item, appendMode, oldSelectedObjects, objectsToDeselectMask, objectsToDispatchSelectMessage);
      }
    } else if (this.isAppendSelectionForObjectAllowed(this.selectionModel.selectedObjects, selectionId)) {
      if (appendMode) {
        const objectIndex: number = ArrayUtils.indexOf(this.selectionModel.selectedObjects, selectionId);
        if (objectIndex === -1) {
          this.selectionModel.selectedObjects.push(selectionId);
          objectsToDispatchSelectMessage.push(selectionId);
          // this.dispatchSelectObjectMessages(objectId);
        }
      } else {
        this.selectionModel.selectedObjects.push(selectionId);
        const objectIndex: number = ArrayUtils.indexOf(oldSelectedObjects, selectionId);
        if (objectIndex === -1) {
          objectsToDispatchSelectMessage.push(selectionId);
          // this.dispatchSelectObjectMessages(objectId);
        } else {
          objectsToDeselectMask[objectIndex] = false;
        }
      }
    }
  }

  private isMetaElementSelectionAvailable(metaUsageId: MetaElementUsageIdentityVO, appendMode: boolean, forceTextureSelection: boolean): boolean {
    if (this.projectModel.isAdminView) {
      return true;
    }
    const connectionCube: Connection = this.connectionInfoService.findConnectionByIdentity(metaUsageId);
    if (MetaElementUtils.isDyeable(connectionCube.metaElementVO)) {
      return true;
    }
    if (connectionCube.type === Connection.TYPE_IMAGE && !ConnectionInfoUtils.hasDynamicPinReference(connectionCube.metaElementVO)) {
      return false;
    }
    // check connection "isNotSelectable" property
    if (!ConnectionInfoUtils.isConnectionSelectable(connectionCube.metaElementVO)) {
      return false;
    } else if (!(connectionCube.metaElementVO instanceof TextureAreaVO) || MetaElementUtils.isMultipurposeArea(connectionCube.metaElementVO)) {
      // we can select all connections without any restrictions except of texture areas which are not part of multipurpose areas
      return true;
    }

    // Breakable bridge pins can be selected, if the already selected connections are also breakable bridge pins.
    if (this.isBreakableBridgePin(connectionCube)) {
      return this.getConnections().every((cn) => this.isBreakableBridgePin(cn));
    }

    // texture area can be selected if another texture area is already selected
    // or if append mode is not enabled and article that contains given texture area is already selected
    return (
      forceTextureSelection ||
      this.getTextConnections().length > 0 ||
      (!appendMode && this.selectionModel.getArticleUsageIds().indexOf(metaUsageId.articleUsageId) >= 0)
    );
  }

  /**
   * Test whether the connection is a breakable Jumper Pin.
   * @param connection The connection to identify.
   * @returns true, if the connection is a breakable Jumper Pin.
   */
  private isBreakableBridgePin(connection: Connection): boolean {
    // For a connection to be a breakable bridge pin,
    // - it must have dynamic pin reference
    // - it must be of type IMAGE
    // - the related article must be of type BRIDGE
    return ConnectionInfoUtils.hasDynamicPinReference(connection.metaElementVO)
      && ConnectionInfoUtils.isConnectionOfType(connection, [Connection.TYPE_IMAGE])
      && connection.articleContainer.article.type.id == ArticleTypeIds.BRIDGE;
  }

  private createArticleSelectionIdByMetaElementUsageIdentity(metaUsageId: MetaElementUsageIdentityVO): SelectionArticleGroupIdentity {
    const articleUsage: ArticleUsageVO = this.projectModel.getArticleUsage(metaUsageId.articleUsageId);
    return this.projectHelper.createSelection(articleUsage);
  }

  private createSelectionIdForShiftMultiSelectionForConnections(metaUsageId: MetaElementUsageIdentityVO): SelectionId | undefined {
    const firstConnection = this.getFirstConnection();
    if (!firstConnection) {
      return undefined;
    }
    const multiSelectionDecisionProperty = this.findMultiSelectionDecisionPropertyForConnection(firstConnection);
    const lastConnection: Connection = this.connectionInfoService.findConnectionByIdentity(metaUsageId);
    if (
      lastConnection == null ||
      (firstConnection.metaElementVO instanceof ConnectionAreaVO && !(lastConnection.metaElementVO instanceof ConnectionAreaVO)) ||
      (firstConnection.metaElementVO instanceof TextureAreaVO && !(lastConnection.metaElementVO instanceof TextureAreaVO))
    ) {
      return undefined;
    }
    const collidedConnections: Connection[] = this.calculateCollidedConnectionsForSelection(firstConnection, lastConnection);
    return this.createSelectionIdsByConnectionsForShiftMultiSelectionForConnections(collidedConnections, multiSelectionDecisionProperty);
  }

  private createSelectionIdForShiftMultiSelectionForArticles(selectionId: SelectionId | undefined): SelectionId | undefined {
    if (!selectionId) {
      return undefined;
    }
    let targetSelectionId: SelectionId | undefined;
    if (selectionId instanceof Array) {
      if (selectionId.length !== 1) {
        return selectionId;
      }
      targetSelectionId = selectionId[0];
    } else {
      targetSelectionId = selectionId;
    }

    if (!targetSelectionId || !(targetSelectionId instanceof SelectionArticleGroupIdentity)) {
      return undefined;
    }

    const articleObjectId: SelectionArticleGroupIdentity = targetSelectionId;
    if (ArrayUtils.isEmpty(articleObjectId.articleUsageIds)) {
      return undefined;
    }

    const firstArticleContainer = this.getArticleConnectedToRangeOnLineArticle(this.selectionModel.getArticleUsageIds());
    const lastArticleContainer = this.getArticleConnectedToRangeOnLineArticle(articleObjectId.articleUsageIds);
    if (!firstArticleContainer && !lastArticleContainer) {
      return this.getSelectAccessoryArticles(articleObjectId);
    }
    if (
      !firstArticleContainer ||
      !lastArticleContainer ||
      !this.articleContainerUtils.isAllArticlesConnectedToSameRangeOnLineContainer([firstArticleContainer, lastArticleContainer])
    ) {
      return undefined;
    }

    const insertArticleHelper: InsertArticleHelper = this.insertArticleHelperFactory.createInstanceByRangeOnLineArticleContainer(
      this.articleContainerService.getBaseRangeOnLineContainer(firstArticleContainer)
    );
    const containers: ArticleContainer[] = insertArticleHelper.getConnectedArticlesBetweenContainers(
      firstArticleContainer,
      lastArticleContainer,
      null,
      true
    );
    const newSelectionIds: SelectionId[] = [];
    for (const container of containers) {
      const selectionGroupIdentity: SelectionArticleGroupIdentity = this.projectHelper.createSelection(container.articleUsage);
      newSelectionIds.push(selectionGroupIdentity);
    }
    return newSelectionIds;
  }

  private getSelectAccessoryArticles(articleGroupId: SelectionArticleGroupIdentity): SelectionId | undefined {
    const containers: ArticleContainer[] = [];
    const firstArticleContainer = this.getArticleConnectedToRangeOnLineArticle(this.selectionModel.getArticleUsageIds(), false);
    const lastArticleContainer = this.getArticleConnectedToRangeOnLineArticle(articleGroupId.articleUsageIds, false);
    if (!firstArticleContainer || !lastArticleContainer) {
      return undefined;
    }
    const firstConnection = this.getFirstBackwardAcceptingCube(firstArticleContainer.articleUsage);
    const lastConnection = this.getFirstBackwardAcceptingCube(lastArticleContainer.articleUsage);
    const multiSelectionDecisionProperty = this.findMultiSelectionDecisionPropertyForConnection(firstConnection);
    if (!firstConnection || !lastConnection) {
      return undefined;
    }
    if (!this.isMetaElementAllowedForShiftMultiSelectionByDecisionProperty(lastConnection, multiSelectionDecisionProperty)) {
      return undefined;
    }

    const collidedConnections: Connection[] = this.calculateCollidedConnectionsForSelection(firstConnection, lastConnection);

    const collidedConnectionsThatCanBeSelectedTogether: Connection[] = collidedConnections
      .filter((connection) =>
        connection.metaElementVO instanceof ConnectionAreaVO &&
        connection.metaElementVO.connectionSide === ConnectionAreaVO.SIDE_ACCEPTING &&
        this.isAccessoryCompositionSelectionAvailable(connection.articleContainer) &&
        this.isMetaElementAllowedForShiftMultiSelectionByDecisionProperty(connection, multiSelectionDecisionProperty));

    const connectionAreasThatCanBeSelectedTogether: ConnectionAreaUsageVO[] = collidedConnectionsThatCanBeSelectedTogether
      .map((connection) => connection.metaElementUsage)
      .filter((metaElement): metaElement is ConnectionAreaUsageVO => metaElement instanceof ConnectionAreaUsageVO);

    for (const connectionAreaUsage of connectionAreasThatCanBeSelectedTogether) {
      for (const usageIdentity of connectionAreaUsage.connection.givingAreaIds) {
        const accessoryContainer = this.containerService.findArticleContainerInGlobal(usageIdentity.articleUsageId);
        if (accessoryContainer != null && !containers.includes(accessoryContainer)) {
          containers.push(accessoryContainer);
        }
      }
    }

    return containers.map((container) => this.projectHelper.createSelection(container.articleUsage));
  }

  private isAccessoryCompositionSelectionAvailable(articleContainer: ArticleContainer): boolean {
    let result: boolean = true;
    if (!this.compositeArticleInfoUtils.isCompositePartArticle(articleContainer)) {
      return result;
    }
    if (!this.projectHelper.isCompositePartArticleSelectionAllowed(articleContainer)) {
      const baseContainers: ArticleContainer[] = this.containerService.getBaseCompositePartArticles(articleContainer);
      result = !this.articleContainerUtils.containsArticleConnectedToMountingRail(baseContainers);
    }
    return result;
  }

  private getFirstBackwardAcceptingCube(articleUsage: ArticleUsageVO): Connection | undefined {
    const parentArticleMetaUsageIds: MetaElementUsageIdentityVO[] = ArticleUsageUtils.getBackwardConnectedConnectionUsageIds(articleUsage);
    for (const metaUsageId of parentArticleMetaUsageIds) {
      const parentArticleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(metaUsageId.articleUsageId);
      if (!parentArticleContainer) {
        return undefined;
      }
      const acceptingCube: Connection = parentArticleContainer.findFirstConnectionByName(metaUsageId.metaElementName);
      if (!acceptingCube) {
        continue;
      }
      return acceptingCube;
    }
    return undefined;
  }

  private createSelectionIdsByConnectionsForShiftMultiSelectionForConnections(
    connectionCubes: Connection[],
    multiSelectionDecisionProperty: PropertyVO | undefined
  ): SelectionMetaElementUsageIdentity[] {
    const selectionMetaElements: SelectionMetaElementUsageIdentity[] = [];
    for (const cube of connectionCubes) {
      if (
        this.isMetaElementAllowedForShiftMultiSelectionByDecisionProperty(cube, multiSelectionDecisionProperty) &&
        ((cube.metaElementVO instanceof ConnectionAreaVO &&
            MetaElementUsageUtils.isFreeConnectionUsage(
              cube.metaElementUsage,
              (cube.metaElementVO as ConnectionAreaVO).connectionMultiplicity
            )) ||
          (cube.metaElementVO instanceof TextureAreaVO && cube.isActive && (cube.mesh.material as Material).visible))
      ) {
        const selectionId: SelectionMetaElementUsageIdentity = new SelectionMetaElementUsageIdentity(
          this.containerService.getArticleContainer(cube).articleGUID,
          cube
        );
        selectionMetaElements.push(selectionId);
      }
    }
    return selectionMetaElements;
  }

  private isMetaElementAllowedForShiftMultiSelectionByDecisionProperty(
    connectionCube: Connection,
    controlMultiSelectionDecisionProperty: PropertyVO | undefined
  ): boolean {
    const multiSelectionDecisionProperty = this.findMultiSelectionDecisionPropertyForConnection(connectionCube);
    if (!controlMultiSelectionDecisionProperty && !multiSelectionDecisionProperty) {
      return true;
    }
    return PropertyUtils.propertiesShareCommonPropertyValue(controlMultiSelectionDecisionProperty, multiSelectionDecisionProperty);
  }

  private findMultiSelectionDecisionPropertyForConnection(connection: Connection | undefined): PropertyVO | undefined {
    if (!connection || !connection.metaElementVO) {
      return undefined;
    }
    if (ConnectionInfoUtils.hasLabelingAccessoryCategory(connection.metaElementVO)) {
      // in case connection is for labeling, we do not limit selection based on type property,
      // so that no matter what (different) type of labeling material can be applied, they all can be selected together by the user
      // as such selection can be displayed together in label editor (might be displayed in mutiple different tabs in label editor)
      return undefined;
    }
    let property: PropertyVO | undefined;
    if (connection.metaElementVO && connection.metaElementVO instanceof ConnectionAreaVO) {
      const connectionArea: ConnectionAreaVO = connection.metaElementVO as ConnectionAreaVO;
      property = PropertyUtils.getPropertyByName(connectionArea.connectionRequirements, ArticleConnectionPropertyDef.TYPE);
    }
    if (!property && connection.articleContainer && connection.articleContainer.article) {
      property = PropertyUtils.getPropertyByName(connection.articleContainer.article.connectionRequirements, ArticleConnectionPropertyDef.TYPE);
    }
    return property;
  }

  private dispatchSelectObjectMessages(selectionIds: readonly SelectionId[]): SelectionData[] {

    const selectionData: SelectionData[] = [];

    for (const selectionId of selectionIds) {
      if (selectionId instanceof SelectionArticleGroupIdentity) {
        for (const usageId of selectionId.articleUsageIds) {
          const articleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(usageId);
          if (articleContainer) {
            articleContainer.articleSelectedHandler();
          }
        }
        selectionData.push({selectionId, isSelected: true, selectionType: "article"});
      } else if (selectionId instanceof MetaElementUsageIdentityVO) {
        const metaElementIdentity: MetaElementUsageIdentityVO = selectionId as MetaElementUsageIdentityVO;
        const articleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(metaElementIdentity.articleUsageId);
        if (articleContainer) {
          articleContainer.metaElementSelectedHandler(metaElementIdentity);
        }
        selectionData.push({selectionId, isSelected: true, selectionType: "metaElement"});
      }
    }

    return selectionData;
  }

  private calculateCollidedConnectionsForSelection(firstConnection: Connection, lastConnection: Connection) {
    // Deactivated because of new issues reported in 37045:
    // const collidedConnections: Connection[] = CollisionHelper.findCollisionConnectionsInGlobal(firstConnection, lastConnection, true, true, false, false);
    const collidedConnections: Connection[] = CollisionHelper.findCollisionConnectionsInGlobal(
      firstConnection,
      lastConnection,
      this.containerService,
      true,
      false,
      false,
      true
    );
    /* Deactivated because of 37045 as a precaution - because there might also be issues with this:
    const firstConnectionCorridor: number = this.getSelectionCorridorForConnection(firstConnection, 0);
    if (firstConnectionCorridor !== 0) {
      const lastConnectionCorridor: number = this.getSelectionCorridorForConnection(lastConnection, firstConnectionCorridor);
      const minCorridor: number = Math.min(firstConnectionCorridor, lastConnectionCorridor);
      const maxCorridor: number = Math.max(firstConnectionCorridor, lastConnectionCorridor);
      const collidedConnectionsInZDirection: Connection[] = CollisionHelper.findCollisionConnectionsInGlobal(firstConnection, lastConnection, true, false, true, false);
      const collidedConnectionsInZDirectionWithSuitableCorridor: Connection[] = [];
      for (const collidedConnectionInZDirection of collidedConnectionsInZDirection) {
        const collidedConnectionCorridor: number = this.getSelectionCorridorForConnection(collidedConnectionInZDirection, NaN);
        if (!isNaN(collidedConnectionCorridor) && collidedConnectionCorridor <= maxCorridor && collidedConnectionCorridor >= minCorridor) {
          collidedConnectionsInZDirectionWithSuitableCorridor.push(collidedConnectionInZDirection);
        }
      }
      ArrayUtils.mergeArrays(collidedConnections, collidedConnectionsInZDirectionWithSuitableCorridor);
    } */
    return collidedConnections;
  }

  private isAppendSelectionForObjectAllowed(selectionIds: SelectionId[], selectionId: SelectionId): boolean {
    if (ArrayUtils.isEmpty(selectionIds)) {
      return true;
    }
    const firstSelectionId: SelectionId = selectionIds[0];
    if (firstSelectionId instanceof SelectionArticleGroupIdentity && selectionId instanceof SelectionArticleGroupIdentity) {
      return true;
    } else if (firstSelectionId instanceof MetaElementUsageIdentityVO && selectionId instanceof MetaElementUsageIdentityVO) {
      return true;
    } else {
      return false;
    }
  }

  private getFirstConnection(): Connection | undefined {
    const selectedConnections: Connection[] = this.getConnections();
    return selectedConnections[0];
  }

  private getArticleConnectedToRangeOnLineArticle(articleUsageIds: string[], useRangeOnLineProperty: boolean = true): ArticleContainer | undefined {
    if (ArrayUtils.isEmpty(articleUsageIds)) {
      return undefined;
    }
    for (const articleUsageId of articleUsageIds) {
      const articleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(articleUsageId);
      if (useRangeOnLineProperty) {
        if (this.articleConnectionService.isArticleConnectedToRangeOnLineArticleWithConfigured(articleContainer)) {
          return this.getBaseArticleContainer(articleContainer);
        }
      } else {
        if (!this.articleConnectionService.isArticleConnectedToRangeOnLineArticleWithConfigured(articleContainer)) {
          return this.getBaseArticleContainer(articleContainer);
        }
      }
    }
    return undefined;
  }

  private getBaseArticleContainer(articleContainer: ArticleContainer): ArticleContainer {
    let baseContainer: ArticleContainer = articleContainer;
    if (
      !this.compositeArticleInfoUtils.isCompositePartArticle(articleContainer) ||
      this.projectHelper.isCompositePartArticleSelectionAllowed(articleContainer)
    ) {
      return baseContainer;
    }
    const baseContainers: ArticleContainer[] = this.containerService.getBaseCompositePartArticles(articleContainer);
    if (baseContainers.length > 0) {
      baseContainer = baseContainers[0];
    }
    return baseContainer;
  }

  public selectArticlesByUsageIds(articleUsageIds: string[], ctrlClamped: boolean = false): void {
    const selectionIds: SelectionArticleGroupIdentity[] = [];
    const processedUsageIds: string[] = [];
    for (const usageId of articleUsageIds) {
      if (processedUsageIds.indexOf(usageId) > -1) {
        continue;
      }
      const articleToSelect: ArticleContainer = this.containerService.findArticleContainerInGlobal(usageId);
      if (articleToSelect && !articleToSelect.articleSelected) {
        const articleUsage: ArticleUsageVO = articleToSelect.articleUsage;
        const selectionGroupIdentity: SelectionArticleGroupIdentity = this.projectHelper.createSelection(articleUsage);
        selectionIds.push(selectionGroupIdentity);
        ArrayUtils.mergeArrays(processedUsageIds, selectionGroupIdentity.articleUsageIds || []);
      }
    }
    this.selectObject(selectionIds, ctrlClamped, false);
  }

  public selectAllArticles(): void {
    const selectionIds: SelectionArticleGroupIdentity[] = [];
    for (const articleUsage of this.containerService.enumerateArticleContainersInGlobal()
      .filter((container) => container.article.articleNo !== ArticleConstants.FREE_MOUNTING_ARTICLE_NO)
      .map((container) => container.articleUsage)) {
      const selectionGroupIdentity: SelectionArticleGroupIdentity = this.projectHelper.createSelection(articleUsage);
      selectionIds.push(selectionGroupIdentity);
    }
    this.selectObject(selectionIds, false, false);
  }

  public getTextConnections(): Connection[] {
    return this.getConnectionsByType(Connection.TEXT_TYPES);
  }

  public getConnectionsByType(connectionTypes: string[]): Connection[] {
    const result: Connection[] = [];
    for (const cube of this.getConnections()) {
      if (ConnectionInfoUtils.isConnectionOfType(cube, connectionTypes)) {
        result.push(cube);
      }
    }
    return result;
  }

  public unSelectObject(selectionId: SelectionId): void {
    if (!this.isSelectionEnabled()) {
      return;
    }
    if (selectionId instanceof MetaElementUsageIdentityVO) {
      const expandedSelectionIds: SelectionId[] = [];
      this.addSelectionIdsBySet(selectionId, expandedSelectionIds);
      for (const objectIdInSet of expandedSelectionIds) {
        this.unSelectSingleObject(objectIdInSet);
      }
    } else {
      this.unSelectSingleObject(selectionId);
    }
  }

  private unSelectSingleObject(selectionId: SelectionId): void {
    const objectIdIndex: number = ArrayUtils.indexOf(this.selectionModel.selectedObjects, selectionId);
    if (objectIdIndex >= 0) {
      this.selectionModel.selectedObjects.splice(objectIdIndex, 1);
      this.dispatchObjectMessages([], [selectionId]);
    }
  }

  private addSelectionIdsBySet(selectionId: MetaElementUsageIdentityVO, resultSelectionIds: SelectionId[]): void {
    const container: ArticleContainer = this.containerService.findArticleContainerInGlobal(selectionId.articleUsageId);
    if (!container) {
      return;
    }
    const selectedCube: Connection = container.findFirstConnectionByName(selectionId.metaElementName);
    if (!selectedCube || this.isConnectionSelected(selectedCube, resultSelectionIds)) {
      return;
    }
    resultSelectionIds.push(selectionId);
    const cubeSelectionSetIds: string[] = ConnectionInfoUtils.getSelectionSetIdsByConnection(selectedCube);
    if (cubeSelectionSetIds.length > 0) {
      const cubes: Connection[] = ConnectionInfoUtils.getFreeAcceptingConnections(container);
      for (const cube of cubes) {
        if (this.isConnectionSelected(cube, resultSelectionIds)) {
          continue;
        }
        if (this.isConnectionIncludedInSelectionSet(cube, cubeSelectionSetIds)) {
          const newSelectionId: SelectionMetaElementUsageIdentity = new SelectionMetaElementUsageIdentity(container.articleGUID, cube);
          resultSelectionIds.push(newSelectionId);
        }
      }
    }
  }

  private isConnectionIncludedInSelectionSet(connectionCube: Connection, selectionSetIds: string[]): boolean {
    if (!connectionCube || !selectionSetIds) {
      return false;
    }
    const cubeSelectionSetIds: string[] = ConnectionInfoUtils.getSelectionSetIdsByConnection(connectionCube);
    for (const cubeSelectionSetId of cubeSelectionSetIds) {
      if (selectionSetIds.indexOf(cubeSelectionSetId) !== -1) {
        return true;
      }
    }
    return false;
  }

  private isConnectionSelected(connectionCube: Connection, selectionIds: SelectionId[]): boolean {
    for (const selectionId of selectionIds) {
      if (selectionId instanceof SelectionMetaElementUsageIdentity && selectionId.connectionUid === connectionCube.uid) {
        return true;
      }
    }
    return false;
  }

  private dispatchUnselectObjectMessages(selectionIds: readonly SelectionId[]): SelectionData[] {

    const selectionData: SelectionData[] = [];

    for (const selectionId of selectionIds) {
      if (selectionId instanceof SelectionArticleGroupIdentity) {
        for (const usageId of selectionId.articleUsageIds) {
          const articleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(usageId);
          if (articleContainer) {
            articleContainer.articleDeselectedHandler();
          }
        }
        selectionData.push({selectionId, isSelected: false, selectionType: "article"});
      } else if (selectionId instanceof MetaElementUsageIdentityVO) {
        const articleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(selectionId.articleUsageId);
        if (articleContainer) {
          articleContainer.metaElementDeselectedHandler(selectionId);
        }
        selectionData.push({selectionId, isSelected: false, selectionType: "metaElement"});
      }
    }

    return selectionData;
  }

  public removeAllSelections(): void {
    if (ArrayUtils.isEmpty(this.selectionModel.selectedObjects)) {
      return;
    }
    const objectsToDeselect: SelectionId[] = this.selectionModel.selectedObjects.concat();
    this.selectionModel.selectedObjects = [];
    this.dispatchObjectMessages([], objectsToDeselect);
    this.selectionModel.requestClearSelection();
  }

  public updateSelectionOnUndoRedoChange(): void {
    setTimeout(() => {
      if (this.selectionModel.isConnectionSelection() && this.getConnections().some((cn) => !cn.isActive || !(cn.mesh.material as Material).visible)) {
        this.removeAllSelections();
      }
    });
  }

  public getConnections(): Connection[] {
    const result: Connection[] = [];
    for (const selectionId of this.selectionModel.selectedObjects) {
      if (selectionId instanceof MetaElementUsageIdentityVO) {
        const cube: Connection = this.connectionInfoService.findConnectionByIdentity(selectionId as MetaElementUsageIdentityVO);
        if (cube) {
          result.push(cube);
        }
      }
    }
    return result;
  }

  public getArticleContainers(): ArticleContainer[] {
    const articleGUIDs: string[] = [];
    for (const selectionId of this.selectionModel.selectedObjects) {
      if (selectionId instanceof SelectionArticleGroupIdentity) {
        for (const guid of selectionId.articleUsageIds) {
          if (articleGUIDs.indexOf(guid) === -1) {
            articleGUIDs.push(guid);
          }
        }
      }
    }
    return this.containerService.findArticleContainersInGlobalByGuids(articleGUIDs);
  }

  private dispatchObjectMessages(objectsToSelect: readonly SelectionId[], objectsToDeselect: readonly SelectionId[]): void {

    const selectionData: SelectionData[] = [];

    if (objectsToSelect.length > 0) {
      selectionData.push(...this.dispatchSelectObjectMessages(objectsToSelect));
    }
    if (objectsToDeselect.length > 0) {
      selectionData.push(...this.dispatchUnselectObjectMessages(objectsToDeselect));
    }

    this.selectionModel.notifySelectionChanged(selectionData);

    if (this.accessoryEditorStateManager.editorState === AccessoryEditorState.OPEN) {
      this.accessoryEditorStateManager.updateAccessorySelection(true, objectsToDeselect.length > 0);
    }
  }
}
